import { useEffect, useState } from "react";
import { Controller, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Select, { components } from "react-select";
import { ChevronDown } from "lucide-react";
import CustomCheckbox from "../Common/CustomCheckbox";
import ErrorMessage from "../Common/ErrorMessage";
import PostEmbed from "./PostEmbed";
import { defaultReactSelectStyles } from "../../utils/reactSelectStyles";
import { getSocialPlatformList } from "../../api/postgre/socialPlatformApi";

const DropdownIndicator = (props) => (
    <components.DropdownIndicator {...props}>
        <ChevronDown className="h-4 w-4 text-slate-400" />
    </components.DropdownIndicator>
);

const contentSelectStyles = {
    ...defaultReactSelectStyles,
    control: (base) => ({
        ...defaultReactSelectStyles.control(base),
        backgroundColor: "#f7f7f7",
    }),
};

const detectPlatformFromUrl = (url, options = []) => {
    if (!url) return null;
    const lower = url.toLowerCase();
    if (lower.includes("instagram.com")) return options.find((p) => p.value.toLowerCase() === "instagram") || null;
    if (lower.includes("tiktok.com") || lower.includes("vm.tiktok.com") || lower.includes("vt.tiktok.com")) return options.find((p) => p.value.toLowerCase() === "tiktok") || null;
    return null;
};

const ContentFormFieldsEdit = ({
    control,
    setValue,
    errors = {},
    showTerms = true,
    showTitle = true,
}) => {
    const { t } = useTranslation();

    const fileTypeOptions = [
        { value: "Video", label: t("MY_CONTENT_FILE_TYPE_VIDEO") },
        { value: "Image", label: t("MY_CONTENT_FILE_TYPE_IMAGE") },
    ];

    const postTypeOptions = [
        { value: "Post", label: t("MY_CONTENT_POST_TYPE_POST") },
        { value: "Story", label: t("MY_CONTENT_POST_TYPE_STORY") },
    ];
    const [platformOptions, setPlatformOptions] = useState([]);
    const linkValue = useWatch({ control, name: "link" });

    useEffect(() => {
        getSocialPlatformList()
            .then((list) => {
                setPlatformOptions(list.map((p) => ({ value: p.name, label: p.name })));
            })
            .catch(() => {});
    }, []);

    useEffect(() => {
        if (!setValue) return;
        const detected = detectPlatformFromUrl(linkValue, platformOptions);
        if (detected) {
            setValue("platform", detected, { shouldValidate: true });
        }
    }, [linkValue, setValue, platformOptions]);

    return (
        <div className="space-y-4">
            {showTitle && (
                <div>
                    <Controller
                        name="title"
                        control={control}
                        rules={{ required: t("MY_CONTENT_VALIDATION_TITLE_REQUIRED") }}
                        render={({ field }) => (
                            <input
                                {...field}
                                type="text"
                                placeholder={t("MY_CONTENT_PLACEHOLDER_TITLE")}
                                className="w-full rounded-md border border-slate-200 bg-input-bg px-4 py-3 text-sm outline-none focus:outline-none"
                            />
                        )}
                    />
                    <ErrorMessage message={errors.title?.message} />
                </div>
            )}
            <div>
                <Controller
                    name="link"
                    control={control}
                    rules={{
                        required: t("MY_CONTENT_VALIDATION_LINK_REQUIRED"),
                        validate: (value) => {
                            if (!value) return true;
                            if (!/^https?:\/\/\S+$/i.test(value)) return t("MY_CONTENT_VALIDATION_LINK_INVALID");
                            const lower = value.toLowerCase();
                            const isInstagram = lower.includes("instagram.com");
                            const isTikTok = lower.includes("tiktok.com") || lower.includes("vm.tiktok.com") || lower.includes("vt.tiktok.com");
                            if (!isInstagram && !isTikTok) {
                                return t("CONTENT_VALIDATION_LINK_PLATFORM");
                            }
                            return true;
                        },
                    }}
                    render={({ field }) => {
                        const isShortTikTok = field.value && (field.value.toLowerCase().includes("vm.tiktok.com") || field.value.toLowerCase().includes("vt.tiktok.com"));
                        return (
                            <>
                                <div className="relative">
                                    <input
                                        {...field}
                                        type="text"
                                        placeholder={t("MY_CONTENT_PLACEHOLDER_LINK")}
                                        className={`w-full rounded-md border border-slate-200 bg-input-bg px-4 py-3 text-sm outline-none focus:outline-none ${isShortTikTok ? "pr-24" : ""}`}
                                    />
                                    {isShortTikTok && (
                                        <a
                                            href={field.value}
                                            target="_blank"
                                            rel="noreferrer"
                                            onClick={(e) => e.stopPropagation()}
                                            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-white hover:opacity-90"
                                        >
                                            {t("CONTENT_VIEW_LINK")}
                                        </a>
                                    )}
                                </div>
                                {!isShortTikTok && (
                                    <div className="mt-3">
                                        <PostEmbed link={field.value} />
                                    </div>
                                )}
                            </>
                        );
                    }}
                />
                <ErrorMessage message={errors.link?.message} />
            </div>
            <div>
                <Controller
                    name="platform"
                    control={control}
                    rules={{ required: t("MY_CONTENT_VALIDATION_PLATFORM_REQUIRED") }}
                    render={({ field }) => (
                        <Select
                            {...field}
                            options={platformOptions}
                            placeholder={t("MY_CONTENT_PLACEHOLDER_PLATFORM")}
                            classNamePrefix="react-select"
                            components={{ DropdownIndicator }}
                            styles={contentSelectStyles}
                        />
                    )}
                />
                <ErrorMessage message={errors.platform?.message} />
            </div>
            <div>
                <Controller
                    name="fileType"
                    control={control}
                    rules={{ required: t("MY_CONTENT_VALIDATION_FILE_TYPE_REQUIRED") }}
                    render={({ field }) => (
                        <Select
                            {...field}
                            options={fileTypeOptions}
                            placeholder={t("MY_CONTENT_PLACEHOLDER_FILE_TYPE")}
                            classNamePrefix="react-select"
                            components={{ DropdownIndicator }}
                            styles={contentSelectStyles}
                        />
                    )}
                />
                <ErrorMessage message={errors.fileType?.message} />
            </div>
            <div>
                <Controller
                    name="postType"
                    control={control}
                    rules={{ required: t("MY_CONTENT_VALIDATION_POST_TYPE_REQUIRED") }}
                    render={({ field }) => (
                        <Select
                            {...field}
                            options={postTypeOptions}
                            placeholder={t("MY_CONTENT_PLACEHOLDER_POST_TYPE")}
                            classNamePrefix="react-select"
                            components={{ DropdownIndicator }}
                            styles={contentSelectStyles}
                        />
                    )}
                />
                <ErrorMessage message={errors.postType?.message} />
            </div>
            <div>
                <Controller
                    name="caption"
                    control={control}
                    render={({ field }) => (
                        <textarea
                            {...field}
                            placeholder={t("MY_CONTENT_PLACEHOLDER_CAPTION")}
                            rows={4}
                            className="w-full rounded-md border border-slate-200 bg-input-bg px-4 py-3 text-sm outline-none focus:outline-none"
                        />
                    )}
                />
            </div>
            {showTerms && (
                <div className="flex flex-col items-center">
                    <Controller
                        name="acceptedTerms"
                        control={control}
                        rules={{ required: t("AUTH_VALIDATION_ACCEPT_TERMS_REQUIRED") }}
                        render={({ field }) => (
                            <CustomCheckbox
                                name={field.name}
                                checked={field.value}
                                onChange={field.onChange}
                                label={(
                                    <span>
                                        {t("AUTH_REGISTER_ACCEPT_TERMS_PREFIX")}{" "}
                                        <a
                                            href="/terms"
                                            target="_blank"
                                            rel="noreferrer"
                                            className="underline underline-offset-4 text-primary"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            {t("COMMON_TERMS_AND_CONDITIONS")}
                                        </a>
                                    </span>
                                )}
                            />
                        )}
                    />
                    <ErrorMessage message={errors.acceptedTerms?.message} />
                </div>
            )}
        </div>
    );
};

const ContentFormFields = ({
    mode = "edit",
    control,
    setValue,
    errors = {},
    data = {},
    showTerms = true,
    showTitle = true,
}) => {
    const { t } = useTranslation();

    if (mode === "view") {
        return (
            <div className="space-y-4 text-sm text-slate-600">
                <div className="rounded-md border border-slate-200 bg-input-bg px-4 py-3">
                    <p className="text-xs text-slate-400">{t("MY_CONTENT_FIELD_CONTENT_LINK")}</p>
                    <p className="mt-1 break-all text-sm text-primary underline underline-offset-4">
                        {data.link}
                    </p>
                </div>
                <PostEmbed link={data.link} />
                <div className="rounded-md border border-slate-200 bg-input-bg px-4 py-3">
                    <p className="text-xs text-slate-400">{t("MY_CONTENT_FIELD_PLATFORM")}</p>
                    <p className="mt-1 text-sm">{data.platform}</p>
                </div>
                <div className="rounded-md border border-slate-200 bg-input-bg px-4 py-3">
                    <p className="text-xs text-slate-400">{t("MY_CONTENT_FIELD_FILE_TYPE")}</p>
                    <p className="mt-1 text-sm">{data.fileType}</p>
                </div>
                <div className="rounded-md border border-slate-200 bg-input-bg px-4 py-3">
                    <p className="text-xs text-slate-400">{t("MY_CONTENT_FIELD_POST_TYPE")}</p>
                    <p className="mt-1 text-sm">{data.postType}</p>
                </div>
                <div className="rounded-md border border-slate-200 bg-input-bg px-4 py-3">
                    <p className="text-xs text-slate-400">{t("MY_CONTENT_FIELD_CAPTION")}</p>
                    <p className="mt-1 text-sm">{data.caption}</p>
                </div>
            </div>
        );
    }

    return <ContentFormFieldsEdit control={control} setValue={setValue} errors={errors} showTerms={showTerms} showTitle={showTitle} />;
};

export default ContentFormFields;
